import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "~/lib/utils";

import type { AlignedTrendPoint, HealthGrade } from "./accountManagementTypes";

export { SortHeading, compareNullableSortValues } from "./dashboardTableSorting";

export function count(value: unknown): string {
  return Math.round(Number(value) || 0).toLocaleString();
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return "—";
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

export function months(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Number(value).toFixed(1)} mo`;
}

export function freshness(value: number | null | undefined): string {
  return value === null || value === undefined ? "No data" : `${value}d`;
}

export function navigatorNames(value: unknown): string[] {
  return String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

export function navigatorSummary(value: unknown): string {
  const names = navigatorNames(value);
  if (!names.length) return "Unassigned";
  if (names.length <= 2) return names.join(", ");
  return `${names[0]} +${names.length - 1}`;
}

export function sourceLabel(source: string): string {
  return source === "live" ? "Live · Supabase read-only" : "Data: MOCK";
}

export const HEALTH_RANK = { "at-risk": 0, watch: 1, monitoring: 2, healthy: 3 } as const;
export const HEALTH_COLORS = {
  healthy: "#10b981",
  watch: "#f59e0b",
  "at-risk": "#dc4c35",
  monitoring: "#94a3b8",
} as const;

export function alignmentTone(
  rate: number,
  thresholds: { alignmentTargetRate: number; alignmentAtRiskRate: number },
): "good" | "warn" | "at-risk" {
  if (rate >= thresholds.alignmentTargetRate) return "good";
  return rate >= thresholds.alignmentAtRiskRate ? "warn" : "at-risk";
}

export function freshnessTone(
  days: number | null,
  thresholds: { freshnessAtRiskDays: number; freshnessWatchDays: number },
): "good" | "warn" | "at-risk" {
  if (days === null || days > thresholds.freshnessAtRiskDays) return "at-risk";
  return days > thresholds.freshnessWatchDays ? "warn" : "good";
}

export function missingMbiTone(
  rate: number | null,
  thresholds: { missingMbiAtRiskRate: number; missingMbiWatchRate: number },
): "good" | "warn" | "at-risk" | "neutral" {
  if (rate === null) return "neutral";
  if (rate >= thresholds.missingMbiAtRiskRate) return "at-risk";
  return rate >= thresholds.missingMbiWatchRate ? "warn" : "good";
}

export function accessTone(value: string, noTone: "at-risk" | "warn" = "at-risk") {
  if (value === "yes") return "good" as const;
  return value === "no" ? noTone : ("neutral" as const);
}

export function accessLabel(prefix: string, value: string): string {
  if (value === "yes") return `${prefix} yes`;
  if (value === "no") return `${prefix} no`;
  return `${prefix} unknown`;
}

export function toneDotClass(tone: string): string {
  if (tone === "good") return "inline-block h-2 w-2 rounded-full bg-emerald-500";
  if (tone === "warn") return "inline-block h-2 w-2 rounded-full bg-amber-500";
  if (tone === "at-risk") return "inline-block h-2 w-2 rounded-full bg-red-500";
  return "inline-block h-2 w-2 rounded-full bg-slate-400";
}

export function statusPillClass(tone: string): string {
  if (tone === "good") {
    return "inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800";
  }
  if (tone === "warn") {
    return "inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800";
  }
  if (tone === "at-risk") {
    return "inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700";
  }
  return "inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700";
}

export function barFillClass(tone: string): string {
  if (tone === "good") return "block h-full rounded-full bg-emerald-500";
  if (tone === "warn") return "block h-full rounded-full bg-amber-500";
  if (tone === "at-risk") return "block h-full rounded-full bg-red-500";
  return "block h-full rounded-full bg-slate-400";
}

export function meterTrackClass(className = ""): string {
  return ["block h-1.5 overflow-hidden rounded-full bg-muted", className].filter(Boolean).join(" ");
}

export function HealthPill({ grade }: { grade: HealthGrade }) {
  const labels = {
    healthy: "Healthy",
    watch: "Watch",
    monitoring: "Monitoring",
    "at-risk": "At risk",
  } as const;
  const tone =
    grade === "healthy"
      ? "good"
      : grade === "watch"
        ? "warn"
        : grade === "monitoring"
          ? "neutral"
          : "at-risk";
  return <span className={statusPillClass(tone)}>{labels[grade]}</span>;
}

export function TrafficLight({
  tone = "neutral",
  label,
  title,
}: {
  tone?: string;
  label: string;
  title?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={title}>
      <i className={toneDotClass(tone)} aria-hidden="true" />
      {label}
    </span>
  );
}

export function ColumnHelp({ label, definition }: { label: string; definition: string }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    above: boolean;
    left: number;
    top: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipId = useId();

  function showTooltip() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 250;
    const left = Math.max(8, Math.min(rect.left - 8, window.innerWidth - width - 8));
    const above = rect.bottom + 110 > window.innerHeight;
    setPosition({ left, top: above ? rect.top - 7 : rect.bottom + 7, above });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  return (
    <span className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] text-muted-foreground"
        aria-label={`About ${label}: ${definition}`}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setOpen(false)}
        onFocus={showTooltip}
        onBlur={() => setOpen(false)}
        onClick={showTooltip}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        ?
      </button>
      {open && position
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className="fixed z-50 max-w-[250px] rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground shadow-md"
              style={{
                left: position.left,
                top: position.top,
                transform: position.above ? "translateY(-100%)" : undefined,
              }}
            >
              {definition}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

export function TrendSparkline({ points, label }: { points: AlignedTrendPoint[]; label: string }) {
  const values = points.map((point) => Number(point.alignedPatients) || 0);
  if (!values.length) return <span className="text-xs text-muted-foreground">—</span>;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const spread = Math.max(maximum - minimum, 1);
  const coordinates = values
    .map((value, index) => {
      const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
      const y = 28 - ((value - minimum) / spread) * 22;
      return `${x},${y}`;
    })
    .join(" ");
  const increasing = (values.at(-1) ?? 0) >= (values[0] ?? 0);

  return (
    <svg
      className={
        increasing
          ? "h-8 w-20 fill-none stroke-emerald-600 stroke-[2]"
          : "h-8 w-20 fill-none stroke-red-600 stroke-[2]"
      }
      viewBox="0 0 100 32"
      role="img"
      aria-label={`${label}: ${values.join(", ")}`}
    >
      <polyline points={coordinates} />
      <circle
        cx={values.length === 1 ? 50 : 100}
        cy={28 - (((values.at(-1) ?? 0) - minimum) / spread) * 22}
        r="2.5"
      />
    </svg>
  );
}

export function AccountTableFrame(props: {
  "aria-label": string;
  children: ReactNode;
  minWidthClass: string;
}) {
  return (
    <div className="max-h-[min(68vh,40rem)] overflow-auto rounded-md border border-border/60">
      <table
        aria-label={props["aria-label"]}
        className={cn("w-full border-collapse text-[13px] leading-snug", props.minWidthClass)}
      >
        {props.children}
      </table>
    </div>
  );
}

export function accountHeadCellClass(sticky = false): string {
  return cn(
    "sticky top-0 z-[2] border-b border-border/70 bg-background px-3 py-2.5 text-left align-bottom text-xs font-medium whitespace-nowrap text-muted-foreground",
    sticky && "left-0 z-[3] min-w-[13.75rem] shadow-[1px_0_0_0_var(--border)]",
  );
}

export function accountBodyCellClass(options?: { numeric?: boolean; sticky?: boolean }): string {
  return cn(
    "border-b border-border/40 bg-background px-3 py-3 align-top group-hover:bg-muted/30",
    options?.sticky && "sticky left-0 z-[1] min-w-[13.75rem] shadow-[1px_0_0_0_var(--border)]",
    options?.numeric && "whitespace-nowrap tabular-nums",
  );
}

export function MetricStack(props: { detail?: ReactNode; nowrap?: boolean; value: ReactNode }) {
  return (
    <div className={cn("flex flex-col gap-0.5", props.nowrap ? "whitespace-nowrap" : "min-w-0")}>
      <span className="font-medium text-foreground tabular-nums">{props.value}</span>
      {props.detail ? (
        <span
          className={cn(
            "text-[11px] text-muted-foreground tabular-nums",
            props.nowrap ? "whitespace-nowrap" : "leading-snug",
          )}
        >
          {props.detail}
        </span>
      ) : null}
    </div>
  );
}

export function AccountPanelHeading(props: { children: ReactNode; meta?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
      <h2 className="text-base font-semibold tracking-tight text-foreground">{props.children}</h2>
      {props.meta ? <span className="text-xs text-muted-foreground">{props.meta}</span> : null}
    </div>
  );
}
